import React, { useState } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getOccasions, getActiveOccasions } from 'wasp/client/operations';
import { DashboardLayout } from '../../client/components/Sidebar';
import { OccasionCard } from './OccasionCard';
import { Calendar, Filter, Loader } from 'lucide-react';
type OccasionWithType = {
  id: string;
  name: string;
  slug: string;
  occasionTypeId: string;
  communityMonth: number | null;
  communityDay: number | null;
  durationDays: number | null;
  startDate: Date | null;
  endDate: Date | null;
  description: string | null;
  significance: string | null;
  imageUrl: string | null;
  isActive: boolean;
  occasionType: {
    id: string;
    key: string;
    label: string;
    description: string | null;
    icon: string | null;
  };
};

export default function OccasionListPage() {
  const [filterActive, setFilterActive] = useState<boolean>(false);

  // Fetch occasions
  const {
    data: occasions,
    isLoading,
    error,
  } = useQuery(filterActive ? getActiveOccasions : getOccasions, filterActive ? undefined : {}) as {
    data: OccasionWithType[] | undefined;
    isLoading: boolean;
    error: any;
  };

  return (
    <DashboardLayout>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Community Occasions
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            Browse and explore Community religious occasions, celebrations, and sacred times
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => setFilterActive(false)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              !filterActive
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            All Occasions
          </button>
          <button
            onClick={() => setFilterActive(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterActive
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Active & Upcoming
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-indigo-600" />
            <span className="ml-3 text-gray-600 dark:text-gray-300">
              Loading occasions...
            </span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">
              Error loading occasions: {(error as any).message}
            </p>
          </div>
        )}

        {/* Occasions Grid */}
        {!isLoading && !error && occasions && (
          <>
            {occasions.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-300 text-lg">
                  No occasions found
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
                  Run `wasp db seed` to populate Community occasions
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {occasions.map((occasion) => (
                  <OccasionCard key={occasion.id} occasion={occasion} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}



